import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import Navigation from "@/components/navigation";
import { MapPin, FileText } from "lucide-react";

import ug from "ug-locations";
import { locationData } from "@/lib/location-data";
import { useRwandaLocations } from "@/lib/locationHelper";

// ---------------------------------------------------------------------------
// Zod schema
// Rwanda  → province / district / sector / cell / village
// Uganda  → region / subRegion / district / county / subcounty / parish / village
//
// The repository's flat DB columns for Uganda are: district, subcounty, parish, village.
// region / subRegion / county are UI helpers forwarded as-is in the POST body.
// ---------------------------------------------------------------------------
const clientPropertySchema = z.object({
  upi:          z.string().min(1, "UPI number is required"),
  ownerName:    z.string().min(1, "Owner name is required"),
  ownerIdNumber:z.string().min(1, "ID/TIN number is required"),
  phone:        z.string().min(1, "Phone number is required"),

  country: z.enum(["UG", "RW"]).default("UG"),

  // Rwanda
  province: z.string().optional(),
  district: z.string().optional(),
  sector:   z.string().optional(),
  cell:     z.string().optional(),
  village:  z.string().optional(),

  // Uganda
  region:    z.string().optional(),
  subRegion: z.string().optional(),
  county:    z.string().optional(),
  subcounty: z.string().optional(), // DB column
  parish:    z.string().optional(), // DB column
});

type ClientPropertyFormValues = z.infer<typeof clientPropertySchema>;

// ---------------------------------------------------------------------------
// Static Uganda helpers
// ---------------------------------------------------------------------------
const UGANDA_REGIONS = [
  "Central Region",
  "Eastern Region",
  "Northern Region",
  "Western Region",
];

const getSubRegionsByRegion = (region: string): string[] => {
  switch (region) {
    case "Northern Region": return ["Karamoja", "Acholi", "Lango", "West Nile"];
    case "Eastern Region":  return ["Busoga", "Teso", "Bugisu", "Sebei"];
    case "Western Region":  return ["Bunyoro", "Tooro", "Ankole", "Kigezi"];
    case "Central Region":  return ["Buganda", "Kampala", "Mengo"];
    default:                return [];
  }
};

const getCountiesByDistrict = (district: string): string[] => {
  const countyMap: Record<string, string[]> = {
    // A
    "Abim":           ["Labwor County"],
    "Adjumani":       ["East Moyo County"],
    "Agago":          ["Agago County"],
    "Alebtong":       ["Ajuri County", "Moroto County"],
    "Amolatar":       ["Kioga County"],
    "Amudat":         ["Upe County"],
    "Amuria":         ["Amuria County", "Kapelebyong County"],
    "Amuru":          ["Kilak County"],
    "Apac":           ["Kwania County", "Maruzi County"],
    "Arua":           ["Arua Municipal Council", "Ayivu County", "Madi-Okollo County", "Terego County", "Vurra County"],

    // B
    "Budaka":         ["Budaka County", "Iki-Iki County"],
    "Bududa":         ["Manjiya County"],
    "Bugiri":         ["Bukooli County"],
    "Bugweri":        ["Bugweri County"],
    "Buhweju":        ["Buhweju County"],
    "Buikwe":         ["Buikwe County"],
    "Bukedea":        ["Bukedea County"],
    "Bukomansimbi":   ["Bukomansimbi County"],
    "Bukwo":          ["Kongasis County"],
    "Bulambuli":      ["Bulambuli County"],
    "Buliisa":        ["Buliisa County"],
    "Bundibugyo":     ["Bughendera County", "Bwamba County"],
    "Bushenyi":       ["Bushenyi-Ishaka Municipal Council", "Igara County"],
    "Busia":          ["Busia Municipal Council", "Samia-Bugwe County"],
    "Butaleja":       ["Bunyole County"],
    "Butambala":      ["Butambala County"],
    "Butebo":         ["Butebo County"],
    "Buvuma":         ["Buvuma Islands County"],
    "Buyende":        ["Budiope East County", "Budiope West County"],

    // D
    "Dokolo":         ["Dokolo County"],

    // G
    "Gomba":          ["Gomba County"],
    "Gulu":           ["Aswa County", "Gulu Municipal Council", "Omoro County"],

    // H
    "Hoima":          ["Bugahya County", "Buhaguzi County", "Hoima Municipal Council"],

    // I
    "Ibanda":         ["Ibanda County"],
    "Iganga":         ["Bugweri County", "Iganga Municipal Council", "Kigulu County"],
    "Isingiro":       ["Bukanga County", "Isingiro County"],

    // J
    "Jinja":          ["Butembe County", "Jinja Municipal Council", "Kagoma County"],

    // K
    "Kaabong":        ["Dodoth East County", "Dodoth West County", "Ik County"],
    "Kabale":         ["Kabale Municipal Council", "Ndorwa County", "Rubanda County", "Rukiga County"],
    "Kabarole":       ["Bunyangabu County", "Burahya County", "Fort Portal Municipal Council", "Nakasongola County"],
    "Kaberamaido":    ["Kaberamaido County", "Kalaki County"],
    "Kalangala":      ["Bujumba County", "Kyamuswa County"],
    "Kaliro":         ["Bulamogi County"],
    "Kalungu":        ["Kalungu County"],
    "Kampala":        ["Kampala Capital City"],
    "Kamuli":         ["Bugabula County", "Buzaaya County"],
    "Kamwenge":       ["Kibale County", "Kitagwenda County"],
    "Kanungu":        ["Kinkiizi County"],
    "Kapchorwa":      ["Tingey County"],
    "Kasese":         ["Bukonzo County", "Busongora County", "Kasese Municipal Council"],
    "Katakwi":        ["Toroma County", "Usuk County"],
    "Kayunga":        ["Bbaale County", "Ntenjeru County"],
    "Kibaale":        ["Bugangaizi County", "Bugangaizi East County", "Buyaga County", "Buyaga West County", "Buyanja County"],
    "Kiboga":         ["Kiboga East County"],
    "Kibuku":         ["Kibuku County"],
    "Kiruhura":       ["Kazo County", "Nyabushozi County"],
    "Kiryandongo":    ["Kibanda County"],
    "Kisoro":         ["Bufumbira County East", "Bufumbira County North", "Bufumbira County South"],
    "Kitgum":         ["Chua County"],
    "Koboko":         ["Koboko County"],
    "Kole":           ["Kole County"],
    "Kotido":         ["Jie County"],
    "Kumi":           ["Kumi County"],
    "Kween":          ["Kween County"],
    "Kyankwanzi":     ["Kiboga West County"],
    "Kyegegwa":       ["Kyaka County"],
    "Kyenjojo":       ["Mwenge County"],

    // L
    "Lamwo":          ["Lamwo County"],
    "Lira":           ["Erute County", "Lira Municipal Council"],
    "Luuka":          ["Luuka County"],
    "Luweero":        ["Bamunanika County", "Katikamu County"],
    "Lwengo":         ["Bukoto County"],
    "Lyantonde":      ["Kabula County"],

    // M
    "Manafwa":        ["Bubulo County"],
    "Maracha":        ["Maracha County"],
    "Masaka":         ["Bukoto Central County", "Bukoto East County", "Masaka Municipal Council"],
    "Masindi":        ["Bujenje County", "Buruuli County", "Masindi Municipal Council"],
    "Mayuge":         ["Bunya County"],
    "Mbale":          ["Bungokho County", "Mbale Municipal Council"],
    "Mbarara":        ["Kashari County", "Mbarara Municipal Council", "Rwampara County"],
    "Mitooma":        ["Ruhinda County"],
    "Mityana":        ["Busujju County", "Mityana County"],
    "Moroto":         ["Matheniko County", "Moroto Municipal Council"],
    "Moyo":           ["Obongi County", "West Moyo County"],
    "Mpigi":          ["Mawokota County"],
    "Mubende":        ["Buwekula County", "Kasambya County", "Kassanda County"],
    "Mukono":         ["Mukono County", "Mukono Municipal Council", "Nakifuma Council"],

    // N
    "Nakapiripirit":  ["Chekwii (Kadam) County", "Pian County"],
    "Nakaseke":       ["Nakaseke North County", "Nakaseke South County"],
    "Nakasongola":    ["Budyebo County", "Nakasongola County"],
    "Namayingo":      ["Bukooli Island County", "Bukooli South County"],
    "Namutumba":      ["Busiki County"],
    "Napak":          ["Bokora County"],
    "Nebbi":          ["Jonam County", "Padyere County"],
    "Ngora":          ["Ngora County"],
    "Ntoroko":        ["Ntoroko County"],
    "Ntungamo":       ["Kajara County", "Ntungamo Municipal Council", "Ruhaama County", "Rushenyi County"],
    "Nwoya":          ["Nwoya County"],

    // O
    "Otuke":          ["Otuke County"],
    "Oyam":           ["Oyam County"],

    // P
    "Pader":          ["Aruu County"],
    "Pallisa":        ["Agule County", "Butebo County", "Pallisa County"],

    // R
    "Rakai":          ["Kakuuto County", "Kooki County", "Kyotera County"],
    "Rubirizi":       ["Bunyaruguru County", "Katerera County"],
    "Rukungiri":      ["Rubabo County", "Rujumbura County", "Rukungiri Municipal Council"],

    // S
    "Sembabule":      ["Lwemiyaga County", "Mawogola County"],
    "Serere":         ["Kasilo County", "Serere County"],
    "Sheema":         ["Sheema County"],
    "Sironko":        ["Budadiri County"],
    "Soroti":         ["Soroti County", "Soroti Municipal Council"],

    // T
    "Tororo":         ["Tororo County", "Tororo Municipal Council", "West Budama County"],

    // W
    "Wakiso":         ["Busiro County", "Entebbe Municipal Council", "Kyadondo County"],

    // Y
    "Yumbe":          ["Aringa County"],

    // Z
    "Zombo":          ["Okoro County"],
  };

  for (const [key, value] of Object.entries(countyMap)) {
    if (district.toUpperCase().includes(key.toUpperCase())) return value;
  }

  // Fallback — should rarely trigger given complete coverage above
  return ["County North", "County South", "County East", "County West"];
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const ClientPropertyForm: React.FC = () => {
  const { toast } = useToast();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading,   setIsLoading]   = useState(false);
  const [errorMsg,    setErrorMsg]    = useState("");

  // ── Rwanda location helper (same source as Step1Form) ────────────────────
  const { getProvinces, getDistricts, getSectors, getCells, getVillages } =
    useRwandaLocations(locationData as any);

  // ── Uganda cascade state ──────────────────────────────────────────────────
  const [ugandaAllDistricts,    setUgandaAllDistricts]    = useState<string[]>([]);
  const [selectedUgandaRegion,  setSelectedUgandaRegion]  = useState("");
  const [subRegions,            setSubRegions]            = useState<string[]>([]);
  const [selectedSubRegion,     setSelectedSubRegion]     = useState("");
  const [selectedUgandaDistrict,setSelectedUgandaDistrict]= useState("");
  const [counties,              setCounties]              = useState<string[]>([]);
  const [selectedCounty,        setSelectedCounty]        = useState("");
  const [subcounties,           setSubcounties]           = useState<string[]>([]);
  const [selectedSubcounty,     setSelectedSubcounty]     = useState("");
  const [parishes,              setParishes]              = useState<string[]>([]);
  const [selectedParish,        setSelectedParish]        = useState("");
  const [ugandaVillages,        setUgandaVillages]        = useState<string[]>([]);

  // ── Form (must come before any form.watch calls) ──────────────────────────
  const apiUrl =
    import.meta.env.VITE_API_BASE_URL || "https://valuate-api.vercel.app/api/v1";

  const form = useForm<ClientPropertyFormValues>({
    resolver: zodResolver(clientPropertySchema),
    defaultValues: {
      upi: "", ownerName: "", ownerIdNumber: "", phone: "",
      country: "UG",
      province: "", district: "", sector: "", cell: "", village: "",
      region: "", subRegion: "", county: "", subcounty: "", parish: "",
    },
  });

  // ── Watched values (after useForm) ────────────────────────────────────────
  const country         = form.watch("country");
  const watchedProvince = form.watch("province");
  const watchedDistrict = form.watch("district");
  const watchedSector   = form.watch("sector");
  const watchedCell     = form.watch("cell");
  const currentVillage  = form.watch("village");
  const currentCounty   = form.watch("county");

  // ── Rwanda cascading lists (identical pattern to Step1Form) ───────────────
  const rwProvinces = country === "RW" ? getProvinces() : [];
  const rwDistricts = country === "RW" && watchedProvince
    ? getDistricts(watchedProvince) : [];
  const rwSectors   = country === "RW" && watchedProvince && watchedDistrict
    ? getSectors(watchedProvince, watchedDistrict) : [];
  const rwCells     = country === "RW" && watchedProvince && watchedDistrict && watchedSector
    ? getCells(watchedProvince, watchedDistrict, watchedSector) : [];
  const rwVillages  = country === "RW" && watchedProvince && watchedDistrict && watchedSector && watchedCell
    ? getVillages(watchedProvince, watchedDistrict, watchedSector, watchedCell) : [];

  // Reset dependent Rwanda fields when a parent changes
  useEffect(() => {
    if (country !== "RW") return;
    form.setValue("district", ""); form.setValue("sector", "");
    form.setValue("cell", "");     form.setValue("village", "");
  }, [watchedProvince]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (country !== "RW") return;
    form.setValue("sector", ""); form.setValue("cell", ""); form.setValue("village", "");
  }, [watchedDistrict]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (country !== "RW") return;
    form.setValue("cell", ""); form.setValue("village", "");
  }, [watchedSector]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (country !== "RW") return;
    form.setValue("village", "");
  }, [watchedCell]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load all Uganda districts on mount ───────────────────────────────────
  useEffect(() => {
    const districts: string[] = ug.getDistricts();
    setUgandaAllDistricts(districts.sort());
  }, []);

  // ── Uganda: region → sub-regions, reset below ────────────────────────────
  useEffect(() => {
    if (!selectedUgandaRegion) return;
    form.setValue("region", selectedUgandaRegion);
    setSubRegions(getSubRegionsByRegion(selectedUgandaRegion));
    setSelectedSubRegion(""); setSelectedUgandaDistrict(""); setSelectedCounty("");
    setSelectedSubcounty(""); setSelectedParish("");
    setCounties([]); setSubcounties([]); setParishes([]); setUgandaVillages([]);
    form.setValue("subRegion", ""); form.setValue("district", "");
    form.setValue("county", "");   form.setValue("subcounty", "");
    form.setValue("parish", "");   form.setValue("village", "");
  }, [selectedUgandaRegion]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Uganda: sub-region ───────────────────────────────────────────────────
  useEffect(() => {
    form.setValue("subRegion", selectedSubRegion);
  }, [selectedSubRegion]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Uganda: district → counties ──────────────────────────────────────────
  useEffect(() => {
    if (!selectedUgandaDistrict) return;
    form.setValue("district", selectedUgandaDistrict);
    setCounties(getCountiesByDistrict(selectedUgandaDistrict));
    setSelectedCounty(""); setSelectedSubcounty(""); setSelectedParish("");
    setSubcounties([]); setParishes([]); setUgandaVillages([]);
    form.setValue("county", ""); form.setValue("subcounty", "");
    form.setValue("parish", ""); form.setValue("village", "");
  }, [selectedUgandaDistrict]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Uganda: county → subcounties ─────────────────────────────────────────
  useEffect(() => {
    if (!selectedCounty) { form.setValue("county", ""); return; }
    form.setValue("county", selectedCounty);
    if (selectedUgandaDistrict) {
      setSubcounties(ug.getSubcountiesInDistrict(selectedUgandaDistrict));
    }
    setSelectedSubcounty(""); setSelectedParish(""); setParishes([]); setUgandaVillages([]);
    form.setValue("subcounty", ""); form.setValue("parish", ""); form.setValue("village", "");
  }, [selectedCounty, selectedUgandaDistrict]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Uganda: subcounty → parishes ─────────────────────────────────────────
  useEffect(() => {
    if (!selectedUgandaDistrict || !selectedSubcounty) { form.setValue("subcounty", ""); return; }
    form.setValue("subcounty", selectedSubcounty);
    setParishes(ug.getParishesInSubcounty(selectedUgandaDistrict, selectedSubcounty));
    setSelectedParish(""); setUgandaVillages([]);
    form.setValue("parish", ""); form.setValue("village", "");
  }, [selectedUgandaDistrict, selectedSubcounty]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Uganda: parish → villages ────────────────────────────────────────────
  useEffect(() => {
    if (!selectedUgandaDistrict || !selectedSubcounty || !selectedParish) {
      form.setValue("parish", ""); return;
    }
    form.setValue("parish", selectedParish);
    setUgandaVillages(ug.getVillagesInParish(selectedUgandaDistrict, selectedSubcounty, selectedParish));
    form.setValue("village", "");
  }, [selectedUgandaDistrict, selectedSubcounty, selectedParish]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Country switch ────────────────────────────────────────────────────────
  const handleCountryChange = (value: "RW" | "UG") => {
    form.setValue("country", value);
    if (value === "RW") {
      // Clear Uganda UI state
      setSelectedUgandaRegion(""); setSelectedSubRegion(""); setSelectedUgandaDistrict("");
      setSelectedCounty(""); setSelectedSubcounty(""); setSelectedParish("");
      setSubRegions([]); setCounties([]); setSubcounties([]); setParishes([]); setUgandaVillages([]);
      form.setValue("region", "");    form.setValue("subRegion", "");
      form.setValue("county", "");    form.setValue("subcounty", "");
      form.setValue("parish", "");    form.setValue("village", "");
    } else {
      // Clear Rwanda form values
      form.setValue("province", ""); form.setValue("district", "");
      form.setValue("sector", "");   form.setValue("cell", "");
      form.setValue("village", "");
    }
  };

  // ── Submit ────────────────────────────────────────────────────────────────
 const handleSubmit = async (data: ClientPropertyFormValues) => {
  console.log("Submitting:", data);
  setIsLoading(true);
  setErrorMsg("");

  // Normalize country code before sending
  const countryCodeMap: Record<string, string> = {
    rwanda: "RW",
    uganda: "UG",
  };

  // Build clean payload — only include fields relevant to selected country
  const isRwanda = data.country === "RW";

  const locationFields = isRwanda
    ? {
        province: data.province,
        district: data.district,
        sector: data.sector,
        cell: data.cell,
        village: data.village,
        // Clear Uganda fields explicitly
        region: undefined,
        subRegion: undefined,
        county: undefined,
        subcounty: undefined,
        parish: undefined,
      }
    : {
        region: data.region,
        subRegion: data.subRegion,
        district: data.district,
        county: data.county,
        subcounty: data.subcounty,
        parish: data.parish,
        village: data.village,
        // Clear Rwanda fields explicitly
        province: undefined,
        sector: undefined,
        cell: undefined,
      };

  const payload = {
    upi: data.upi,
    ownerName: data.ownerName,
    ownerIdNumber: data.ownerIdNumber,
    phone: data.phone,
    country: countryCodeMap[data.country] ?? data.country,
    ...locationFields,
    status: "draft",
  };

  console.log("Final payload:", payload);

  try {
    const response = await fetch(`${apiUrl}/properties`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const responseData = await response.json();
    if (!response.ok) throw new Error(responseData.message || `Submission failed: ${response.status}`);
    toast({
      title: "Property Submitted Successfully",
      description: "Our technical team will conduct a field assessment and provide your valuation report.",
    });
    setIsSubmitted(true);
    form.reset();
  } catch (err) {
    const message = err instanceof Error ? err.message : "An unknown error occurred.";
    setErrorMsg(message);
    toast({ title: "Submission Failed", description: message, variant: "destructive" });
  } finally {
    setIsLoading(false);
  }
};

  // ── Success screen ────────────────────────────────────────────────────────
  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card className="text-center">
            <CardContent className="pt-8 pb-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Property Submitted Successfully!</h2>
              <p className="text-gray-600 mb-6">
                Thank you for submitting your property details. Our expert technical team will conduct
                a professional field assessment and provide you with a certified valuation report.
              </p>
              <div className="space-y-2 text-sm text-gray-500">
                <p>• Field assessment will be scheduled within 24-48 hours</p>
                <p>• Valuation report will be completed within 3-5 business days</p>
                <p>• You will receive email notifications on progress updates</p>
              </div>
              <Button onClick={() => (window.location.href = "/")} className="mt-6">
                Return to Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ── Form render ───────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Submit Property for Valuation</h2>
          <p className="text-gray-600">
            Provide your property details and our technical team will conduct a professional assessment.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Property Information</CardTitle>
          </CardHeader>

          <CardContent>
            {errorMsg && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                {errorMsg}
              </div>
            )}

            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">

                {/* ── Property Identification ─────────────────────────── */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-700 pb-2 border-b">
                    <FileText className="h-4 w-4" />
                    Property Identification
                  </div>

                  <FormField control={form.control} name="upi" render={({ field }) => (
                    <FormItem>
                      <FormLabel>UPI Number *</FormLabel>
                      <FormControl><Input placeholder="Enter property UPI number" {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />

                  <FormField control={form.control} name="ownerName" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Registered Owner Name *</FormLabel>
                      <FormControl><Input placeholder="Enter registered owner name" {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />

                  <FormField control={form.control} name="ownerIdNumber" render={({ field }) => (
                    <FormItem>
                      <FormLabel>ID / TIN Number *</FormLabel>
                      <FormControl><Input placeholder="Enter ID or TIN number" {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />

                  <FormField control={form.control} name="phone" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone Number *</FormLabel>
                      <FormControl><Input placeholder="Enter phone number" {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                </div>

                {/* ── Location ────────────────────────────────────────── */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-700 pb-2 border-b">
                    <MapPin className="h-4 w-4" />
                    Location Details
                  </div>

                  {/* Country */}
                  <FormField control={form.control} name="country" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Country *</FormLabel>
                      <FormControl>
                        <Select
                          value={field.value}
                          onValueChange={(v) => { field.onChange(v); handleCountryChange(v as "RW" | "UG"); }}
                        >
                          <SelectTrigger><SelectValue placeholder="Select country" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="RW">Rwanda</SelectItem>
                            <SelectItem value="UG">Uganda</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />

                  {/* ── RWANDA – fully cascading dropdowns (mirrors Step1Form) ── */}
                  {country === "RW" && (
                    <>
                      {/* Province */}
                      <FormField control={form.control} name="province" render={({ field }) => (
                        <FormItem>
                          <FormLabel>Province *</FormLabel>
                          <Select value={field.value ?? ""} onValueChange={field.onChange}>
                            <SelectTrigger><SelectValue placeholder="Select Province" /></SelectTrigger>
                            <SelectContent>
                              {rwProvinces.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )} />

                      {/* District */}
                      <FormField control={form.control} name="district" render={({ field }) => (
                        <FormItem>
                          <FormLabel>District *</FormLabel>
                          <Select value={field.value ?? ""} onValueChange={field.onChange} disabled={!watchedProvince}>
                            <SelectTrigger><SelectValue placeholder="Select District" /></SelectTrigger>
                            <SelectContent>
                              {rwDistricts.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )} />

                      {/* Sector */}
                      <FormField control={form.control} name="sector" render={({ field }) => (
                        <FormItem>
                          <FormLabel>Sector *</FormLabel>
                          <Select value={field.value ?? ""} onValueChange={field.onChange} disabled={!watchedDistrict}>
                            <SelectTrigger><SelectValue placeholder="Select Sector" /></SelectTrigger>
                            <SelectContent>
                              {rwSectors.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )} />

                      {/* Cell */}
                      <FormField control={form.control} name="cell" render={({ field }) => (
                        <FormItem>
                          <FormLabel>Cell *</FormLabel>
                          <Select value={field.value ?? ""} onValueChange={field.onChange} disabled={!watchedSector}>
                            <SelectTrigger><SelectValue placeholder="Select Cell" /></SelectTrigger>
                            <SelectContent>
                              {rwCells.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )} />

                      {/* Village */}
                      <FormField control={form.control} name="village" render={({ field }) => (
                        <FormItem>
                          <FormLabel>Village *</FormLabel>
                          <Select value={field.value ?? ""} onValueChange={field.onChange} disabled={!watchedCell}>
                            <SelectTrigger><SelectValue placeholder="Select Village" /></SelectTrigger>
                            <SelectContent>
                              {rwVillages.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )} />
                    </>
                  )}

                  {/* ── UGANDA – cascading dropdowns (mirrors Step1Form) ── */}
                  {country === "UG" && (
                    <>
                      {/* Administrative Region */}
                      <FormField control={form.control} name="region" render={({ field }) => (
                        <FormItem>
                          <FormLabel>Administrative Region *</FormLabel>
                          <Select
                            value={selectedUgandaRegion}
                            onValueChange={(v) => { setSelectedUgandaRegion(v); field.onChange(v); }}
                          >
                            <SelectTrigger><SelectValue placeholder="Select Administrative Region" /></SelectTrigger>
                            <SelectContent>
                              {UGANDA_REGIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )} />

                      {/* Sub-region (optional) */}
                      <FormField control={form.control} name="subRegion" render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            Sub-region <span className="text-gray-400 font-normal">(Optional)</span>
                          </FormLabel>
                          <Select
                            value={selectedSubRegion}
                            onValueChange={(v) => { setSelectedSubRegion(v); field.onChange(v); }}
                            disabled={!selectedUgandaRegion}
                          >
                            <SelectTrigger><SelectValue placeholder="Select Sub-region" /></SelectTrigger>
                            <SelectContent>
                              {subRegions.map((sr) => <SelectItem key={sr} value={sr}>{sr}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )} />

                      {/* District – all districts from ug-locations */}
                      <FormField control={form.control} name="district" render={({ field }) => (
                        <FormItem>
                          <FormLabel>District *</FormLabel>
                          <Select
                            value={selectedUgandaDistrict}
                            onValueChange={(v) => { setSelectedUgandaDistrict(v); field.onChange(v); }}
                            disabled={!selectedUgandaRegion}
                          >
                            <SelectTrigger><SelectValue placeholder="Select District" /></SelectTrigger>
                            <SelectContent>
                              {ugandaAllDistricts.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )} />

                      {/* County – dropdown if predefined, free-text fallback */}
                      <FormField control={form.control} name="county" render={({ field }) => (
                        <FormItem>
                          <FormLabel>County *</FormLabel>
                          {counties.length > 0 ? (
                            <Select
                              value={selectedCounty}
                              onValueChange={(v) => { setSelectedCounty(v); field.onChange(v); }}
                              disabled={!selectedUgandaDistrict}
                            >
                              <SelectTrigger><SelectValue placeholder="Select County" /></SelectTrigger>
                              <SelectContent>
                                {counties.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input
                              placeholder="Enter county name"
                              value={field.value ?? ""}
                              disabled={!selectedUgandaDistrict}
                              onChange={(e) => { field.onChange(e.target.value); setSelectedCounty(e.target.value); }}
                            />
                          )}
                          <FormMessage />
                        </FormItem>
                      )} />

                      {/* Subcounty → maps to DB column `subcounty` */}
                      <FormField control={form.control} name="subcounty" render={({ field }) => (
                        <FormItem>
                          <FormLabel>Subcounty *</FormLabel>
                          <Select
                            value={selectedSubcounty}
                            onValueChange={(v) => { setSelectedSubcounty(v); field.onChange(v); }}
                            disabled={!selectedCounty && !currentCounty}
                          >
                            <SelectTrigger><SelectValue placeholder="Select Subcounty" /></SelectTrigger>
                            <SelectContent>
                              {subcounties.map((sc) => <SelectItem key={sc} value={sc}>{sc}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )} />

                      {/* Parish → maps to DB column `parish` */}
                      <FormField control={form.control} name="parish" render={({ field }) => (
                        <FormItem>
                          <FormLabel>Parish *</FormLabel>
                          <Select
                            value={selectedParish}
                            onValueChange={(v) => { setSelectedParish(v); field.onChange(v); }}
                            disabled={!selectedSubcounty}
                          >
                            <SelectTrigger><SelectValue placeholder="Select Parish" /></SelectTrigger>
                            <SelectContent>
                              {parishes.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )} />

                      {/* Village */}
                      <FormField control={form.control} name="village" render={({ field }) => (
                        <FormItem>
                          <FormLabel>Village *</FormLabel>
                          <Select
                            value={field.value ?? ""}
                            onValueChange={field.onChange}
                            disabled={!selectedParish}
                          >
                            <SelectTrigger><SelectValue placeholder="Select Village" /></SelectTrigger>
                            <SelectContent>
                              {ugandaVillages.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )} />

                      {/* Location summary card */}
                      {selectedUgandaRegion && selectedUgandaDistrict &&
                        (selectedCounty || currentCounty) &&
                        selectedSubcounty && selectedParish && currentVillage && (
                        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <p className="text-sm font-medium text-blue-800 mb-1">Selected Location:</p>
                          <p className="text-sm text-blue-600 leading-6">
                            <span className="font-semibold">Region:</span> {selectedUgandaRegion}<br />
                            {selectedSubRegion && <><span className="font-semibold">Sub-region:</span> {selectedSubRegion}<br /></>}
                            <span className="font-semibold">District:</span> {selectedUgandaDistrict}<br />
                            <span className="font-semibold">County:</span> {selectedCounty || currentCounty}<br />
                            <span className="font-semibold">Subcounty:</span> {selectedSubcounty}<br />
                            <span className="font-semibold">Parish:</span> {selectedParish}<br />
                            <span className="font-semibold">Village:</span> {currentVillage}
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* ── Actions ─────────────────────────────────────────── */}
                <div className="flex justify-end space-x-4 pt-6 border-t">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => { form.reset(); handleCountryChange("RW"); }}
                  >
                    Reset Form
                  </Button>
                  <Button type="submit" disabled={isLoading} className="min-w-[120px]">
                    {isLoading ? "Submitting…" : "Submit Property"}
                  </Button>
                </div>

              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ClientPropertyForm;